<template>
    <div>
        <l-feature-group
            ref="featureGroup"
            layerType="overlay"
            :name="'Relays'"
        >
            <l-layer-group ref="clusterLayer" />
            <l-layer-group ref="pinnedLayer" />
        </l-feature-group>
    </div>
</template>

<script>
import Supercluster from 'supercluster'
import L from 'leaflet'
import { LFeatureGroup, LLayerGroup } from '@vue-leaflet/vue-leaflet'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import { getRelayScore, getColorForScore } from '@/components/Map/utils/relayScore.js'

const CLUSTER_SIZES = {
    LARGE: 48,
    MEDIUM: 40,
    SMALL: 32,
    TINY: 24,
}

const POLLING_INTERVAL_SECONDS = 60
let pollingTimeout = null
let indexAbortController = null
let singleAbortController = null

export default {
    components: {
        LFeatureGroup,
        LLayerGroup,
    },

    mixins: [needsOrganizationSlug],

    props: {
        focusedRelayId: {
            type: Number,
            required: false,
            default: null,
        },
    },

    emits: [
        'fetch-start',
        'fetch-completed',
        'fetch-focused-relay-error',
        'fetch-all-relays-error',
        'fetch-focused-relay-completed',
        'selected-relay-changed',
    ],

    expose: ['refreshData', 'clearSelectedRelay'],

    data() {
        return {
            relayItems: new Map(),
            clusters: [],
            pollingEnabled: false,
            initialLoadFinished: false,
            supercluster: null,
            map: null,
            clusterLayer: null,
            pinnedLayer: null,
            pinnedRelayItem: null,
            selectedRelay: null,
        }
    },

    computed: {
        relayItemArray() {
            return Array.from(this.relayItems.values())
        },
    },

    async mounted() {
        this.$nextTick(async () => {
            const featureGroup = this.$refs.featureGroup?.leafletObject

            this.clusterLayer = this.$refs.clusterLayer.leafletObject
            this.pinnedLayer = this.$refs.pinnedLayer.leafletObject
            this.map = featureGroup._map

            this.supercluster = new Supercluster({
                radius: 70,
                maxZoom: 18,
                nodeSize: 32,
            })

            this.map.on('zoomend', this.updateRelayMarkers)
            await this.startPolling()
        })
    },

    unmounted() {
        this.stopPolling()
        indexAbortController?.abort()
        singleAbortController?.abort()
    },

    methods: {
        /**
         * Polling
         */
        async startPolling() {
            if (pollingTimeout) {
                clearTimeout(pollingTimeout)
                pollingTimeout = null
            }

            this.pollingEnabled = true
            await this.fetchRelays()

            pollingTimeout = setTimeout(this.startPolling, POLLING_INTERVAL_SECONDS * 1000)
        },
        stopPolling() {
            if (pollingTimeout) {
                clearTimeout(pollingTimeout)
                pollingTimeout = null
            }

            this.pollingEnabled = false
        },
        async refreshData() {
            this.stopPolling()

            this.relayItems.clear()
            this.initialLoadFinished = false

            await this.startPolling()
        },

        /**
         * Data Fetching
         */
        async fetchRelay(id) {
            try {
                singleAbortController = new AbortController()

                const response = await this.$http.get(`/${this.organizationSlug}/relays/${id}`, {
                    signal: singleAbortController.signal,
                })

                singleAbortController = null

                if (response.status === 200) {
                    const relay = response.data
                    if (this.validateRelayData(relay)) {
                        const relayItem = this.buildRelayItem(relay)
                        this.relayItems.set(relay.id, relayItem)

                        if (this.selectedRelay?.id === relay.id) {
                            this.setSelectedRelay(relay)
                        }
                    }
                }
            } catch (error) {
                // this is handled in the calling function
            } finally {
                singleAbortController = null
            }
        },
        async fetchPageOfRelays(page, relayIdsToDelete) {
            let isLastPage = true

            const searchParams = new URLSearchParams({
                page,
                per_page: 200,
            })

            const response = await this.$http.get(`/${this.organizationSlug}/relays/map?${searchParams.toString()}`, {
                signal: indexAbortController.signal,
            })

            indexAbortController = null

            if (response.status === 200) {
                const { data, last_page: lastPage } = response.data
                isLastPage = page === lastPage
                data.filter((relay) => this.validateRelayData(relay)).forEach((relay) => {
                    const relayItem = this.buildRelayItem(relay)
                    relayIdsToDelete.delete(relay.id)
                    this.relayItems.set(relay.id, relayItem)
                    if (relay.id === this.focusedRelayId) {
                        this.setPinnedRelayItem(relayItem)
                    }

                    if (relay.id === this.selectedRelay?.id) {
                        this.setSelectedRelay(relay)
                    }
                })
            } else {
                throw new Error('Invalid API response')
            }

            return isLastPage
        },
        async fetchRelays() {
            let page = 0
            let isLastPage = true

            this.$emit('fetch-start')

            try {
                if (!this.initialLoadFinished && this.focusedRelayId) {
                    await this.loadFocusedRelay()
                }

                const relayIdsToDelete = new Set(Array.from(this.relayItems.keys()))

                do {
                    page++
                    indexAbortController = new AbortController()
                    // eslint-disable-next-line no-await-in-loop
                    isLastPage = await this.fetchPageOfRelays(page, relayIdsToDelete)
                    this.updateRelayMarkers()
                } while (!isLastPage)

                relayIdsToDelete.forEach((id) => {
                    this.relayItems.delete(id)
                    if (id === this.focusedRelayId) {
                        this.setPinnedRelayItem(null)
                    }

                    if (id === this.selectedRelay.id) {
                        this.setSelectedRelay(null, true)
                    }
                })
                if (relayIdsToDelete.size > 0) {
                    this.updateRelayMarkers()
                }
            } catch (e) {
                if (indexAbortController && !indexAbortController.signal.aborted) {
                    this.$emit('fetch-all-relays-error')
                }
            } finally {
                indexAbortController = null
            }

            if (!this.initialLoadFinished) {
                this.initialLoadFinished = true
            }

            this.$emit('fetch-completed', {
                relays: this.relayItemArray.map(({ relay }) => relay),
                pageCount: page,
            })
        },

        async loadFocusedRelay() {
            if (this.focusedRelayId) {
                await this.fetchRelay(this.focusedRelayId)
                const pinnedRelayItem = this.relayItems.get(this.focusedRelayId)
                if (pinnedRelayItem) {
                    this.setPinnedRelayItem(pinnedRelayItem)
                    this.$emit('fetch-focused-relay-completed', pinnedRelayItem.relay)
                } else {
                    this.setPinnedRelayItem(null)
                    this.$emit('fetch-focused-relay-error')
                }
            }
        },

        /**
         * Misc.
         */
        clearSelectedRelay() {
            this.setSelectedRelay(null)
        },
        setSelectedRelay(relay, relayWasDeleted = false) {
            this.selectedRelay = relay
            this.$emit('selected-relay-changed', { relay, relayWasDeleted })
        },
        validateRelayData(relay) {
            return (
                relay.latitude !== null &&
                relay.latitude >= -90 &&
                relay.latitude <= 90 &&
                relay.longitude !== null &&
                relay.longitude >= -180 &&
                relay.longitude <= 180
            )
        },
        buildRelayItem(relay) {
            const score = getRelayScore(relay)
            const color = getColorForScore(score.value)

            return {
                icon: `/images/relays/map/icon-relay-${relay.type.toLowerCase()}-status-${color}.svg`,
                score,
                relay,
            }
        },

        /**
         * Clustering
         */
        setPinnedRelayItem(relayItem) {
            this.pinnedRelayItem = relayItem
            this.updateRelayMarkers()
        },
        updateRelayMarkers() {
            this.clearRelays()

            // Load relay points into supercluster
            // TODO: do this only if the relay array has changed
            const points = this.relayItemArray.reduce((filtered, relayItem) => {
                const { relay } = relayItem
                if (!this.pinnedRelayItem || relay.id !== this.pinnedRelayItem.relay.id) {
                    filtered.push({
                        type: 'Feature',
                        properties: { id: relay.id, relayItem },
                        geometry: { type: 'Point', coordinates: [relay.longitude, relay.latitude] },
                    })
                }
                return filtered
            }, [])

            this.supercluster.load(points)

            this.clusters = this.supercluster.getClusters(
                [-180, -85, 180, 85], // full world for now
                this.map.getZoom()
            )

            this.renderClustersLayer()
            this.renderPinnedLayer()
        },
        renderClustersLayer() {
            this.clusters.forEach((cluster) => {
                const [lng, lat] = cluster.geometry.coordinates
                const { point_count: pointCount, relayItem } = cluster.properties || {}

                if (pointCount > 0) {
                    // Cluster of relays
                    L.marker([lat, lng], {
                        icon: this.createClusterIcon(cluster),
                        zIndexOffset: 1,
                    })
                        .on('click', () => this.zoomToCluster(cluster))
                        .addTo(this.clusterLayer)
                } else if (relayItem) {
                    // Single relay
                    const marker = this.createRelayMarker(relayItem)
                    marker.addTo(this.clusterLayer)
                }
            })
        },
        renderPinnedLayer() {
            if (this.pinnedRelayItem) {
                const marker = this.createRelayMarker(this.pinnedRelayItem)
                marker.setZIndexOffset(10000).addTo(this.pinnedLayer)
            }
        },
        createRelayMarker(relayItem) {
            const { relay } = relayItem
            return L.marker([relay.latitude, relay.longitude], {
                icon: this.createRelayIcon(relayItem),
                zIndexOffset: 2,
            }).on('click', () => this.setSelectedRelay(relayItem.relay))
        },
        createRelayIcon(relayItem) {
            const { relay } = relayItem

            return L.divIcon({
                html: `
                    <div class="map-marker">
                        <img src="${relayItem.icon}" alt="${relay.site_name}" data-test-id="map-relay-icon-${relay.id}" />
                        <div class="label" data-test-id="map-relay-label-${relay.id}">${relay.site_name}</div>
                    </div>
                `,
                className: 'custom-marker',
                iconSize: [40, 65],
                iconAnchor: [20, 12],
            })
        },
        createClusterIcon(cluster) {
            const { point_count: pointCount } = cluster.properties || {}
            const markersInCluster = this.supercluster.getLeaves(cluster.id, Infinity)
            let iconSize = CLUSTER_SIZES.LARGE
            const allScores = markersInCluster.map((m) => m.properties.relayItem.score.value)
            const lowestScoreColor = getColorForScore(Math.min(...allScores))
            let clusterClass = `relay-cluster relay-cluster-${lowestScoreColor}`

            if (pointCount < 10) {
                clusterClass += ' relay-cluster-tiny'
                iconSize = CLUSTER_SIZES.TINY
            } else if (pointCount < 100) {
                clusterClass += ' relay-cluster-small'
                iconSize = CLUSTER_SIZES.SMALL
            } else if (pointCount < 1000) {
                clusterClass += ' relay-cluster-medium'
                iconSize = CLUSTER_SIZES.MEDIUM
            } else {
                clusterClass += ' relay-cluster-large'
            }

            const clusterStyle = `width:${iconSize}px;height:${iconSize}px;border-radius:${iconSize}px;`
            return L.divIcon({
                html: `
                    <div class="${clusterClass}" style="${clusterStyle}"
                        data-test-id="map-cluster-icon-${cluster.id}">
                        <span class="cluster-label" data-test-id="map-cluster-label-${cluster.id}">${pointCount}</span>
                    </div>`,
                className: '',
                iconSize: [iconSize, iconSize],
                iconAnchor: [iconSize / 2, iconSize / 2],
            })
        },
        zoomToCluster(cluster) {
            const expansionZoom = this.supercluster.getClusterExpansionZoom(cluster.properties.cluster_id)
            const center = cluster.geometry.coordinates.reverse()
            this.map.flyTo(center, expansionZoom)
        },
        clearRelays() {
            this.clusterLayer?.clearLayers()
            this.pinnedLayer?.clearLayers()
        },
    },
}
</script>
<style lang="scss">
#relay-map {
    .map-marker {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 5px;
        height: 65px;

        &:hover > .label {
            border: 1px #211f26 solid;
        }

        img {
            width: 24px !important;
            height: 24px !important;
            z-index: 2;
            margin: 0;
        }

        .label {
            z-index: 1;
            text-align: center;
            line-height: 16px;
            background-color: #211f26;
            color: #f1f1f1;
            border-radius: 8px;
            padding: 8px;
            max-width: 180px;
            text-overflow: ellipsis;
            overflow: hidden;
            text-wrap: nowrap;
        }
    }

    .relay-cluster {
        border: 1px solid black;
        display: flex;
        justify-content: center;
        align-items: center;
        color: #000000;
        box-shadow: 0 4px 4px 0 #00000040;

        &.relay-cluster-grey {
            background-color: #cac4d0;
        }

        &.relay-cluster-green {
            background-color: #84db82;
        }

        &.relay-cluster-yellow {
            background-color: #ffeaa1;
        }

        &.relay-cluster-red {
            background-color: #ffb3af;
        }
    }
}
</style>
